import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, Button, Typography, Space, Empty, Spin, Tag, message, Row, Col, Progress, Table, Radio, Avatar, Tooltip, Badge, Tabs, Select, Modal, Input } from 'antd';
import {
    LeftOutlined,
    EyeOutlined,
    RightOutlined,
    ShareAltOutlined,
    AppstoreOutlined,
    CalendarOutlined,
    FieldTimeOutlined,
    TrophyOutlined,
    BookOutlined,
    ProfileOutlined,
    FileTextOutlined,
    CheckCircleFilled,
    ClockCircleFilled,
    ExclamationCircleFilled,
    UserOutlined,
    WarningOutlined,
    BoldOutlined,
    ItalicOutlined,
    UnorderedListOutlined,
    LinkOutlined,
    RobotOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { getMoodleProgramDetails, getManualModules, getManualCourseProgress, sendProgressReport } from '../services/moodleService';
import ExcelJS from 'exceljs';
import { getTrainingModules, getTrainerLedLeaderboard } from '../services/trainingService';
import { Training, Module, NestedModule, LeaderboardEntry } from '../types/training';
import TrainerLedView from '../components/TrainerLedView';
import Leaderboard from '../components/Leaderboard';
import ParticipantList from '../components/ParticipantList';
import { reconstructFromLiveModules, formatDisplayName, deriveUngradedParticipants } from '../utils/moduleUtils';
import { useAuth } from '../hooks/useAuth';
import { ARGB, HEX, WORKFLOW_BG_HEX, WORKFLOW_FG_HEX, WORKFLOW_LABEL } from '../constants/reportColors';
import { isUnderReview, progressCell } from '../utils/reviewStatus';
import { checkIsAdmin } from '../services/adminService';

const { Title, Text } = Typography;
const TrainerLedTrainingDetails: React.FC = () => {
    const { trainingId } = useParams<{ trainingId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const [training, setTraining] = useState<Training | null>(null);
    const [modules, setModules] = useState<Module[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [isAdmin, setIsAdmin] = useState<boolean>(false);
    const [viewMode, setViewMode] = useState<'individual' | 'consolidated'>('individual');
    const [courseProgress, setCourseProgress] = useState<any>(null);
    const [selectedUser, setSelectedUser] = useState<any>(null);
    const [nestedModules, setNestedModules] = useState<NestedModule[]>([]);
    const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
    const [targetModuleId, setTargetModuleId] = useState<string | null>(null);
    const [selectedModuleId, setSelectedModuleId] = useState<string | null>(null);
    const [shareModalOpen, setShareModalOpen] = useState(false);
    const [stakeholderEmails, setStakeholderEmails] = useState<string[]>([]);
    const [trainerComment, setTrainerComment] = useState('');
    const commentRef = useRef<any>(null);

    const applyFormat = (prefix: string, suffix: string = prefix) => {
        const el: HTMLTextAreaElement | undefined = commentRef.current?.resizableTextArea?.textArea;
        if (!el) return;
        const { selectionStart: s, selectionEnd: e, value } = el;
        const selected = value.slice(s, e);
        const replacement = selected ? `${prefix}${selected}${suffix}` : `${prefix}${suffix}`;
        const next = value.slice(0, s) + replacement + value.slice(e);
        setTrainerComment(next);
        setTimeout(() => {
            el.focus();
            const pos = selected ? s + replacement.length : s + prefix.length;
            el.setSelectionRange(pos, pos);
        }, 0);
    };

    const applyBullet = () => {
        const el: HTMLTextAreaElement | undefined = commentRef.current?.resizableTextArea?.textArea;
        if (!el) return;
        const { selectionStart: s, value } = el;
        const lineStart = value.lastIndexOf('\n', s - 1) + 1;
        const next = value.slice(0, lineStart) + '- ' + value.slice(lineStart);
        setTrainerComment(next);
        setTimeout(() => { el.focus(); el.setSelectionRange(s + 2, s + 2); }, 0);
    };
    const [sharing, setSharing] = useState(false);

    const queryParams = new URLSearchParams(location.search);
    const isAnalyzeMode = queryParams.get('mode') === 'analyze';
    const deepLinkParticipant = queryParams.get('participant') || null;
    const deepLinkAssignmentId = queryParams.get('assignmentId') || null;

    useEffect(() => {
        if (deepLinkAssignmentId) setTargetModuleId(deepLinkAssignmentId);
    }, []);

    // When arriving via a deep-linked email (participant + assignmentId in URL),
    // switch to individual view for that participant once courseProgress is available.
    useEffect(() => {
        if (!deepLinkParticipant || !courseProgress || !isAdmin) return;
        const found = (courseProgress.participants || []).find(
            (p: any) => p.email === deepLinkParticipant
        );
        if (found) {
            setSelectedUser(found);
            setViewMode('individual');
            loadDetails(false, found.userid || found.email);
        }
    }, [courseProgress]);

    useEffect(() => {
        const init = async () => {
            const adminStatus = await checkIsAdmin();
            // Only allow Admin Mode if the user IS an admin AND they arrived via the analyze door
            const canAnalyze = adminStatus && isAnalyzeMode;
            setIsAdmin(canAnalyze);
            if (canAnalyze) {
                setViewMode('consolidated');
            } else {
                setViewMode('individual');
            }
        };
        init();
    }, [isAnalyzeMode]);

    useEffect(() => {
        if (trainingId && user) {
            loadDetails();
        }
        // isAdmin intentionally excluded: it resolves asynchronously (see the
        // init effect above), and including it here caused this whole effect
        // to re-fire - and getMoodleProgramDetails to be re-fetched - purely
        // because isAdmin flipped from false to true, on top of a *second*,
        // independent getMoodleProgramDetails call inside fetchCourseProgress.
        // Course progress now has its own effect below, keyed on isAdmin and
        // reusing the courseId loadDetails() already resolves.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trainingId, user]);

    useEffect(() => {
        if (isAdmin && training?.courseId) {
            fetchCourseProgress(training.courseId);
        }
    }, [isAdmin, training?.courseId]);

    const fetchCourseProgress = async (courseId: number) => {
        try {
            console.log('🔍 [TrainerLedTrainingDetails] Fetching progress for courseId:', courseId);
            const progress = await getManualCourseProgress(courseId);
            console.log('📊 [TrainerLedTrainingDetails] Course Progress Data:', progress);
            setCourseProgress(progress);
        } catch (error) {
            console.error('Error fetching course progress:', error);
        }
    };

    const loadDetails = async (silent: boolean = false, targetUserId?: string | number): Promise<void> => {
        try {
            if (!silent) setLoading(true);
            const currentTraining = await getMoodleProgramDetails(trainingId as string);
            if (!currentTraining) {
                message.error('Training not found');
                navigate('/');
                return;
            }
            setTraining(currentTraining);

            // Fetch leaderboard (credits-based) — only on first load, not silent refreshes
            if (!silent && currentTraining.courseId) {
                getTrainerLedLeaderboard(currentTraining.courseId).then(setLeaderboard).catch(() => {});
            }

            // Prefer numeric Moodle user ID over email to avoid email-resolution failures in production
            const userIdToUse = targetUserId
                || (isAdmin && selectedUser ? (selectedUser.userid || selectedUser.email) : (user?.moodleId || user?.username));
            const response = await getManualModules(currentTraining.courseId || currentTraining.id, userIdToUse);
            const moodleModules = response.modules || [];
            
            const mappedModules: Module[] = moodleModules.map((mod: any) => ({
                moduleId: mod.id.toString(),
                trainingId: currentTraining.trainingId,
                name: mod.title,
                description: mod.content,
                deadline: mod.deadline?.toString(),
                status: mod.status,
                answer: mod.answer,
                submissionid: mod.submissionid,
                introfiles: (mod.introfiles || []).map((f: any) => ({ ...f, userid: parseInt(f.userid) })),
                submissionfiles: (mod.submissionfiles || []).map((f: any) => ({ ...f, userid: parseInt(f.userid) })),
                maxCredits: mod.maxcredits ?? 0,
                achievedCredits: mod.grade >= 0 ? mod.grade : undefined,
                stateDeadlines: mod.statedeadlines ? (() => { try { return JSON.parse(mod.statedeadlines); } catch { return undefined; } })() : undefined,
                workflowEnabled: !!mod.workflowenabled,
                currentState: mod.currentstate || undefined,
                stateHistory: mod.statehistory ? (() => { try { return JSON.parse(mod.statehistory); } catch { return []; } })() : [],
                trainerEval: mod.trainereval || '',
                timeSubmitted: mod.timesubmitted || 0,
                reviewBy: mod.reviewby || '',
                reviewStartedAt: mod.reviewstartedat || 0,
                evalCriteria: mod.evalcriteria ? (() => { try { return JSON.parse(mod.evalcriteria); } catch { return undefined; } })() : undefined,
                extractedQuestions: mod.extractedquestions || undefined,
                questionCount: 0
            }));
            
            console.log('📦 [TrainerLedTrainingDetails] Mapped Modules:', mappedModules.map(m => ({ name: m.name, subId: m.submissionid })));
            setModules(mappedModules);
            setNestedModules(reconstructFromLiveModules(moodleModules));
        } catch (error) {
            console.error('Error loading trainer-led details:', error);
            message.error('Failed to load training details');
        } finally {
            setLoading(false);
        }
    };


    const buildReportBuffer = async (): Promise<ArrayBuffer> => {
        const assignments: any[] = courseProgress?.assignments || [];
        const participants: any[] = courseProgress?.participants || [];
        const programName = training!.trainingName;
        const period = training!.startDate && training!.endDate
            ? `${dayjs(training!.startDate.toString().split('T')[0]).format('DD MMM YYYY')} – ${dayjs(training!.endDate.toString().split('T')[0]).format('DD MMM YYYY')}`
            : 'Evergreen Program';

        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet('Progress Report');

        // Build column list
        // Build module groups — same logic as portal preview (first segment of sectionname only)
        const moduleGroups = new Map<string, any[]>();
        assignments.forEach((a: any) => {
            const key = (a.sectionname || '').split('::')[0] || '__flat__';
            if (!moduleGroups.has(key)) moduleGroups.set(key, []);
            moduleGroups.get(key)!.push(a);
        });
        const hasGroups = !(moduleGroups.size === 1 && moduleGroups.has('__flat__'));
        // Build assignCols in grouped order so data rows align with grouped headers
        const assignCols: { a: any }[] = [];
        Array.from(moduleGroups.values()).forEach(grpAssigns => grpAssigns.forEach(a => assignCols.push({ a })));
        const totalCols = 2 + assignCols.length + 3;

        ws.columns = [
            { width: 26 }, { width: 32 },
            ...assignCols.map(() => ({ width: 22 })),
            { width: 14 }, { width: 12 }, { width: 14 },
        ];

        const hdrBorder = { style: 'thin' as const, color: { argb: ARGB.headerBorder } };
        const fullHdrBorder = { top: hdrBorder, bottom: hdrBorder, left: hdrBorder, right: hdrBorder };
        const dataBorder = { style: 'thin' as const, color: { argb: ARGB.rowBorder } };
        const fullDataBorder = { top: dataBorder, bottom: dataBorder, left: dataBorder, right: dataBorder };

        const styleFixed = (cell: any, value: string, align: 'left' | 'center' = 'center') => {
            cell.value = value;
            cell.font = { bold: true, color: { argb: ARGB.headerText }, size: 10 };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ARGB.headerBg } };
            cell.alignment = { wrapText: true, vertical: 'middle', horizontal: align };
            cell.border = fullHdrBorder;
        };

        // Row 1 — Program title banner
        const titleRow = ws.addRow([programName]);
        ws.mergeCells(1, 1, 1, totalCols);
        titleRow.height = 32;
        const titleCell = titleRow.getCell(1);
        titleCell.font = { bold: true, size: 14, color: { argb: ARGB.titleText } };
        titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ARGB.titleBg } };
        titleCell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };

        // Row 2 — Summary bar
        const summaryRow = ws.addRow([`Period: ${period}   |   Participants: ${participants.length}`]);
        ws.mergeCells(2, 1, 2, totalCols);
        summaryRow.height = 20;
        const sumCell = summaryRow.getCell(1);
        sumCell.font = { size: 11, color: { argb: ARGB.summaryText } };
        sumCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ARGB.summaryBg } };
        sumCell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
        sumCell.border = { bottom: { style: 'thin', color: { argb: hasGroups ? ARGB.groupHeaderBg : ARGB.headerBg } } };

        if (hasGroups) {
            // Row 3 — Module group headers (merged per group)
            const grpRow = ws.addRow(new Array(totalCols).fill(''));
            grpRow.height = 26;

            // Add sub-header row BEFORE setting merges so row 4 exists when merges are applied
            const asgRow = ws.addRow(new Array(totalCols).fill(''));
            asgRow.height = 26;

            // Vertical merges: Name, Email, and summary cols span rows 3-4
            ws.mergeCells(3, 1, 4, 1);
            ws.mergeCells(3, 2, 4, 2);
            const summLabels = ['Total Credits', 'Max Credits', 'Completion %'];
            [totalCols - 2, totalCols - 1, totalCols].forEach(c => ws.mergeCells(3, c, 4, c));

            // Horizontal merges: each module group spans its assignment columns in row 3
            const groupEntries = Array.from(moduleGroups.entries());
            let colOffset = 3;
            groupEntries.forEach(([, assigns]) => {
                if (assigns.length > 1) ws.mergeCells(3, colOffset, 3, colOffset + assigns.length - 1);
                colOffset += assigns.length;
            });

            // Style row 3 (group header)
            styleFixed(grpRow.getCell(1), 'Name', 'left');
            styleFixed(grpRow.getCell(2), 'Email', 'left');
            let grpOffset = 3;
            groupEntries.forEach(([groupName, assigns]) => {
                const cell = grpRow.getCell(grpOffset);
                cell.value = groupName === '__flat__' ? '' : groupName;
                cell.font = { bold: true, color: { argb: ARGB.groupHeaderText }, size: 10 };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ARGB.groupHeaderBg } };
                cell.alignment = { vertical: 'middle', horizontal: 'center' };
                cell.border = fullHdrBorder;
                grpOffset += assigns.length;
            });
            summLabels.forEach((label, i) => styleFixed(grpRow.getCell(totalCols - 2 + i), label));

            // Style row 4 (assignment sub-headers)
            let asgOffset = 3;
            groupEntries.forEach(([, assigns]) => {
                assigns.forEach((a: any) => {
                    const cell = asgRow.getCell(asgOffset++);
                    const deadlineStr = a.deadline > 0 ? dayjs.unix(a.deadline).format('DD MMM') : '-';
                    cell.value = `${a.name}\nDue: ${deadlineStr}`;
                    cell.font = { bold: true, color: { argb: ARGB.subHeaderText }, size: 10 };
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ARGB.subHeaderBg } };
                    cell.alignment = { wrapText: true, vertical: 'middle', horizontal: 'center' };
                    cell.border = fullHdrBorder;
                });
            });
            asgRow.height = 36;

            ws.views = [{ state: 'frozen', xSplit: 2, ySplit: 4 }];
        } else {
            // Single header row (no hierarchy)
            const headerRow = ws.addRow([
                'Name', 'Email',
                ...assignments.map((a: any) => {
                    const deadlineStr = a.deadline > 0 ? dayjs.unix(a.deadline).format('DD MMM') : '-';
                    return `${a.name}\nDue: ${deadlineStr}`;
                }),
                'Total Credits', 'Max Credits', 'Completion %',
            ]);
            headerRow.height = 44;
            headerRow.eachCell((cell, colIdx) => {
                cell.font = { bold: true, color: { argb: ARGB.headerText }, size: 10 };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ARGB.headerBg } };
                cell.alignment = { wrapText: true, vertical: 'middle', horizontal: colIdx <= 2 ? 'left' : 'center' };
                cell.border = fullHdrBorder;
            });
            ws.views = [{ state: 'frozen', xSplit: 2, ySplit: 3 }];
        }

        // Data rows
        for (const p of participants) {
            const modById: Record<number, any> = {};
            for (const m of (p.modules || [])) modById[m.assignmentid] = m;

            let totalCred = 0, maxCred = 0;
            const cellTexts: string[] = [];
            const cellArgb: string[] = [];
            const cellFgArgb: string[] = [];

            for (const { a } of assignCols) {
                const mod = modById[a.id];
                if (!mod) {
                    cellTexts.push('—');
                    cellArgb.push(ARGB.notStartedBg);
                    cellFgArgb.push(ARGB.notStartedText);
                } else {
                    const dateStr = mod.timesubmitted > 0 ? dayjs.unix(mod.timesubmitted).format('DD MMM') : '';
                    const grade = mod.grade >= 0 ? mod.grade : -1;
                    const gradeStr = grade >= 0 && mod.maxcredits > 0 ? `${grade}/${mod.maxcredits} pts` : '';
                    // Same label and colours as the portal preview (incl. "Under review")
                    const cell = progressCell(mod);
                    // No date on "Under review": it would read like "under review since"
                    const text = [cell.label, cell.underReview ? '' : dateStr, gradeStr].filter(Boolean).join('\n');
                    const argb = cell.bgArgb;
                    const fgArgb = cell.fgArgb;
                    cellTexts.push(text);
                    cellArgb.push(argb);
                    cellFgArgb.push(fgArgb);
                    totalCred += grade >= 0 ? grade : 0;
                    maxCred += mod.maxcredits || 0;
                }
            }

            const pct = maxCred > 0 ? Math.round((totalCred / maxCred) * 100) : 0;
            const creditArgb = pct >= 70 ? ARGB.scoreGood : pct >= 40 ? ARGB.scoreWarn : pct > 0 ? ARGB.scoreBad : ARGB.scoreNone;

            const dataRow = ws.addRow([
                p.fullname || '', p.email || '',
                ...cellTexts,
                totalCred, maxCred, `${pct}%`,
            ]);
            dataRow.height = 40;

            dataRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ARGB.rowBg } };
            dataRow.getCell(2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ARGB.rowBg } };

            cellTexts.forEach((_, idx) => {
                const cell = dataRow.getCell(3 + idx);
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: cellArgb[idx] } };
                cell.alignment = { wrapText: true, vertical: 'middle', horizontal: 'center' };
                cell.font = { size: 10, color: { argb: cellFgArgb[idx] } };
            });

            const lastFixed = 2 + assignCols.length;
            dataRow.getCell(lastFixed + 1).font = { bold: true, color: { argb: creditArgb } };
            dataRow.getCell(lastFixed + 2).font = { color: { argb: ARGB.emailText } };
            dataRow.getCell(lastFixed + 3).font = { bold: true, color: { argb: creditArgb } };
            dataRow.getCell(lastFixed + 3).alignment = { horizontal: 'center' };

            dataRow.eachCell(cell => { cell.border = fullDataBorder; });
        }

        return wb.xlsx.writeBuffer() as Promise<ArrayBuffer>;
    };

    const handleDownloadReport = async () => {
        try {
            const buffer = await buildReportBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Progress_Report_${training!.trainingName.replace(/[^a-z0-9]/gi, '_')}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            message.error('Failed to generate report.');
        }
    };

    const handleShareReport = async () => {
        if (!stakeholderEmails.length || !training?.courseId) return;
        setSharing(true);
        try {
            const buffer = await buildReportBuffer();
            const uint8 = new Uint8Array(buffer);
            let b64 = '';
            const chunk = 6144; // must be divisible by 3 to avoid mid-string base64 padding
            for (let i = 0; i < uint8.length; i += chunk) {
                b64 += btoa(String.fromCharCode(...Array.from(uint8.subarray(i, i + chunk))));
            }

            const participants: any[] = courseProgress?.participants || [];
            const totalCred = participants.reduce((s: number, p: any) =>
                s + (p.modules || []).reduce((ps: number, m: any) => ps + (m.grade >= 0 ? m.grade : 0), 0), 0);
            const maxCred = participants.reduce((s: number, p: any) =>
                s + (p.modules || []).reduce((ps: number, m: any) => ps + (m.maxcredits || 0), 0), 0);
            const overallPct = maxCred > 0 ? Math.round((totalCred / maxCred) * 100) : 0;
            const period = training!.startDate && training!.endDate
                ? `${dayjs(training!.startDate.toString().split('T')[0]).format('DD MMM YYYY')} – ${dayjs(training!.endDate.toString().split('T')[0]).format('DD MMM YYYY')}`
                : 'Evergreen Program';

            const result = await sendProgressReport({
                courseid: training!.courseId,
                stakeholder_emails: JSON.stringify(stakeholderEmails),
                attachment_b64: b64,
                attachment_filename: `Progress_Report_${training!.trainingName.replace(/[^a-z0-9]/gi, '_')}.xlsx`,
                program_name: training!.trainingName,
                period,
                participant_count: participants.length,
                overall_pct: overallPct,
                trainer_comment: trainerComment.trim() || undefined,
            });
            message.success(`Report sent to ${result.sent} recipient${result.sent !== 1 ? 's' : ''}`);
            setShareModalOpen(false);
            setStakeholderEmails([]);
            setTrainerComment('');
        } catch {
            message.error('Failed to send report. Please try again.');
        } finally {
            setSharing(false);
        }
    };

    if (loading) return <div style={{ textAlign: 'center', padding: '100px 0' }}><Spin size="large" /></div>;
    if (!training) return <Empty description="Training not found"><Button type="primary" onClick={() => navigate('/')}>Back</Button></Empty>;

    return (
        <div style={{ padding: '10px 20px' }}>
            <style>{`.row-needs-action td { background: #fffbe6 !important; }`}</style>
            <Card
                style={{ marginBottom: '12px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}
                bodyStyle={{ padding: '12px 20px' }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {/* Back + icon + title */}
                    <Button icon={<LeftOutlined />} onClick={() => navigate(isAnalyzeMode ? '/admin' : '/')} style={{ flexShrink: 0 }} />
                    <div style={{ width: 40, height: 40, borderRadius: 10, backgroundColor: '#fff7e6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <AppstoreOutlined style={{ fontSize: 18, color: '#fa541c' }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <Space size={6} style={{ marginBottom: 2 }}>
                            {!training.active && <Tag color="red" style={{ margin: 0, fontSize: 11, lineHeight: '18px' }}>Closed</Tag>}
                            <Tag color="orange" style={{ margin: 0, fontSize: 11, lineHeight: '18px' }}>Trainer Led</Tag>
                            <Text type="secondary" style={{ fontSize: 12 }}>ID: {training.trainingId}</Text>
                        </Space>
                        <Text strong style={{ fontSize: 17, lineHeight: 1.2, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{training.trainingName}</Text>
                    </div>

                    {/* Meta: dates · hours · link · view tag */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0, borderLeft: '1px solid #f0f0f0', paddingLeft: 16 }}>
                        <Text type="secondary" style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                            <CalendarOutlined style={{ marginRight: 4 }} />
                            {training?.startDate && training?.endDate
                                ? `${dayjs(training.startDate.toString().split('T')[0]).format('DD/MM/YYYY')} – ${dayjs(training.endDate.toString().split('T')[0]).format('DD/MM/YYYY')}`
                                : 'Evergreen'}
                        </Text>
                        {training.estimatedEffortHours && (
                            <Text type="secondary" style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                                <FieldTimeOutlined style={{ marginRight: 4 }} />{training.estimatedEffortHours} hrs
                            </Text>
                        )}
                        {(() => {
                            const raw = (training.trainingLink || '').trim();
                            if (raw && raw.toLowerCase() !== 'n/a') {
                                const url = raw.match(/(https?:\/\/[^\s<>"]+)/)?.[1];
                                return url ? (
                                    <Tooltip title={url}>
                                        <a href={url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                                            <LinkOutlined /> Link
                                        </a>
                                    </Tooltip>
                                ) : null;
                            }
                            return null;
                        })()}
                        {isAdmin
                            ? <Tag color="gold" icon={<ProfileOutlined />} style={{ margin: 0 }}>TEACHER VIEW</Tag>
                            : <Tag color="blue" icon={<FileTextOutlined />} style={{ margin: 0 }}>PARTICIPANT VIEW</Tag>
                        }
                    </div>

                    {/* Credits — compact inline */}
                    {!(isAdmin && viewMode === 'consolidated') && (() => {
                        const totalMax = modules.reduce((s, m) => s + (m.maxCredits ?? 0), 0);
                        if (totalMax === 0) return null;
                        const totalAchieved = modules.reduce((s, m) => s + (m.achievedCredits ?? 0), 0);
                        const anyGraded = modules.some(m => m.achievedCredits != null);
                        const pct = totalMax > 0 && anyGraded ? Math.min(100, Math.round((totalAchieved / totalMax) * 100)) : null;
                        const color = pct == null ? '#8c8c8c' : pct >= 70 ? '#52c41a' : pct >= 40 ? '#fa8c16' : '#ff4d4f';
                        return (
                            <div style={{ flexShrink: 0, borderLeft: '1px solid #f0f0f0', paddingLeft: 16, textAlign: 'center' }}>
                                <div style={{ display: 'flex', alignItems: 'baseline', gap: 3, justifyContent: 'center' }}>
                                    <TrophyOutlined style={{ fontSize: 13, color }} />
                                    <Text strong style={{ fontSize: 20, color, lineHeight: 1 }}>{anyGraded ? totalAchieved : '—'}</Text>
                                    <Text type="secondary" style={{ fontSize: 12 }}>/ {totalMax} pts</Text>
                                </div>
                                <Text type="secondary" style={{ fontSize: 11 }}>Credits Earned</Text>
                                {pct != null && <Progress percent={pct} strokeColor={color} trailColor="#f0f0f0" showInfo={false} size="small" style={{ marginTop: 3, marginBottom: 0 }} />}
                            </div>
                        );
                    })()}

                    {/* Share Report */}
                    {isAdmin && (
                        <Button type="primary" icon={<ShareAltOutlined />} onClick={() => setShareModalOpen(true)} disabled={!courseProgress?.participants?.length} style={{ flexShrink: 0 }}>
                            Share Report
                        </Button>
                    )}
                </div>
            </Card>

            <Tabs
                defaultActiveKey="assignments"
                style={{ marginTop: 8 }}
                items={[
                    {
                        key: 'assignments',
                        label: <Space><BookOutlined />Assignments</Space>,
                        children: (
                            <>
                                {isAdmin && (() => {
                                    const participants = courseProgress?.participants || [];
                                    const currentIdx = participants.findIndex((p: any) => p.userid === selectedUser?.userid);
                                    const goTo = (idx: number) => {
                                        const p = participants[idx];
                                        setSelectedUser(p);
                                        setTargetModuleId(selectedModuleId);
                                        loadDetails(false, p.userid || p.email);
                                    };
                                    return (
                                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12, marginTop: 8, gap: 12 }}>
                                            <Radio.Group
                                                value={viewMode}
                                                onChange={e => {
                                                    const val = e.target.value;
                                                    setViewMode(val);
                                                    if (val === 'individual' && !selectedUser && participants.length > 0) {
                                                        const firstUser = participants[0];
                                                        setSelectedUser(firstUser);
                                                        loadDetails(false, firstUser.userid || firstUser.email);
                                                    }
                                                }}
                                                buttonStyle="solid"
                                                size="large"
                                            >
                                                <Radio.Button value="consolidated">Consolidated Progress</Radio.Button>
                                                <Radio.Button value="individual">Individual View</Radio.Button>
                                            </Radio.Group>
                                            {viewMode === 'individual' && participants.length > 0 && (
                                                <>
                                                    <Button icon={<LeftOutlined />} disabled={currentIdx <= 0} onClick={() => goTo(currentIdx - 1)}>Prev</Button>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        {currentIdx >= 0 && (
                                                            <Avatar icon={<UserOutlined />} src={`https://ui-avatars.com/api/?name=${encodeURIComponent(formatDisplayName(participants[currentIdx]?.fullname, participants[currentIdx]?.email) || 'U')}&background=random`} />
                                                        )}
                                                        <Select
                                                            value={currentIdx >= 0 ? currentIdx : undefined}
                                                            onChange={(idx: number) => goTo(idx)}
                                                            style={{ flex: 1, maxWidth: 320 }}
                                                            optionLabelProp="label"
                                                            showSearch
                                                            filterOption={(input, option) =>
                                                                (option?.searchtext as string || '').toLowerCase().includes(input.toLowerCase())
                                                            }
                                                        >
                                                            {participants.map((p: any, i: number) => (
                                                                <Select.Option key={p.userid} value={i} label={formatDisplayName(p.fullname, p.email)} searchtext={`${p.fullname} ${p.email}`}>
                                                                    <Space size={8}>
                                                                        <Avatar size="small" icon={<UserOutlined />} src={`https://ui-avatars.com/api/?name=${encodeURIComponent(formatDisplayName(p.fullname, p.email) || 'U')}&background=random`} />
                                                                        <div>
                                                                            <div style={{ fontSize: 13, fontWeight: 500 }}>{formatDisplayName(p.fullname, p.email)}</div>
                                                                            <div style={{ fontSize: 11, color: '#8c8c8c' }}>{p.email}</div>
                                                                        </div>
                                                                    </Space>
                                                                </Select.Option>
                                                            ))}
                                                        </Select>
                                                        <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{currentIdx + 1} / {participants.length}</Text>
                                                    </div>
                                                    <Button icon={<RightOutlined />} disabled={currentIdx >= participants.length - 1} onClick={() => goTo(currentIdx + 1)}>Next</Button>
                                                </>
                                            )}
                                        </div>
                                    );
                                })()}
                                {isAdmin && viewMode === 'consolidated' ? (
                <Card style={{ borderRadius: '12px' }} title="Student Submission Progress">
                    {(() => {
                        const assignments: any[] = courseProgress?.assignments || [];

                        const STATE_COLOR: Record<string, string> = {
                            proposed:         '#1677ff',
                            proposed_changes: '#fa8c16',
                            updated:          '#722ed1',
                            approved:         '#52c41a',
                            completed:        '#13c2c2',
                        };
                        const STATE_LABEL: Record<string, string> = {
                            proposed:         'Proposed',
                            proposed_changes: 'Changes Req.',
                            updated:          'Updated',
                            approved:         'Approved',
                            completed:        'Completed',
                        };

                        const drillIn = (record: any, assignmentId?: number) => {
                            setSelectedUser(record);
                            setTargetModuleId(assignmentId ? assignmentId.toString() : null);
                            setViewMode('individual');
                            loadDetails(false, record.userid || record.email);
                        };

                        const renderCell = (a: any, record: any) => {
                            const mod = (record.modules || []).find((m: any) => m.assignmentid === a.id);
                            if (!mod) return <span style={{ color: '#d9d9d9' }}>—</span>;

                            const needsAction = mod.workflowenabled && (mod.currentstate === 'proposed' || mod.currentstate === 'updated');
                            const hasGrade = mod.grade != null && mod.grade >= 0;
                            const underReview = isUnderReview(mod);

                            return (
                                <Tooltip title={
                                    <span>
                                        {underReview
                                            ? `Under review${mod.reviewby ? ` by ${mod.reviewby}` : ''}`
                                            : mod.workflowenabled ? (WORKFLOW_LABEL[mod.currentstate] || 'Not started') : mod.status}
                                        {mod.timesubmitted > 0 && !underReview ? ` · ${dayjs.unix(mod.timesubmitted).format('DD MMM')}` : ''}
                                        {hasGrade ? ` · ${mod.grade}/${mod.maxcredits} pts` : ''}
                                    </span>
                                }>
                                    <div
                                        onClick={() => drillIn(record, a.id)}
                                        style={{
                                            cursor: 'pointer',
                                            padding: '4px 6px',
                                            borderRadius: 6,
                                            background: needsAction ? '#fff7e6' : 'transparent',
                                            border: needsAction ? '1px solid #ffd591' : '1px solid transparent',
                                            minWidth: 72,
                                            textAlign: 'center',
                                        }}
                                    >
                                        {underReview ? (
                                            <Tag style={{
                                                fontSize: 10, padding: '0 5px', margin: 0,
                                                background: HEX.underReviewBg,
                                                borderColor: HEX.underReviewText,
                                                color: HEX.underReviewText,
                                            }}>
                                                <EyeOutlined style={{ marginRight: 2 }} />Under review
                                            </Tag>
                                        ) : mod.workflowenabled ? (
                                            mod.currentstate ? (
                                                <Tag style={{
                                                    fontSize: 10, padding: '0 5px', margin: 0,
                                                    background: WORKFLOW_BG_HEX[mod.currentstate] || HEX.notStartedBg,
                                                    borderColor: WORKFLOW_FG_HEX[mod.currentstate] || HEX.notStartedText,
                                                    color: WORKFLOW_FG_HEX[mod.currentstate] || HEX.notStartedText,
                                                }}>
                                                    {needsAction && <WarningOutlined style={{ marginRight: 2 }} />}
                                                    {WORKFLOW_LABEL[mod.currentstate] || mod.currentstate}
                                                </Tag>
                                            ) : (
                                                <ClockCircleFilled style={{ color: '#d9d9d9', fontSize: 14 }} />
                                            )
                                        ) : (
                                            mod.status === 'Submitted' || mod.status === 'Passed'
                                                ? <CheckCircleFilled style={{ color: '#52c41a', fontSize: 14 }} />
                                                : mod.status === 'Draft' || mod.status === 'In Progress'
                                                    ? <ExclamationCircleFilled style={{ color: '#faad14', fontSize: 14 }} />
                                                    : <ClockCircleFilled style={{ color: '#d9d9d9', fontSize: 14 }} />
                                        )}
                                        {hasGrade && (
                                            <div style={{ fontSize: 10, color: '#52c41a', marginTop: 2 }}>
                                                {mod.grade}/{mod.maxcredits}
                                            </div>
                                        )}
                                        {mod.timesubmitted > 0 && !underReview && (
                                            <div style={{ fontSize: 9, color: '#8c8c8c' }}>
                                                {dayjs.unix(mod.timesubmitted).format('DD MMM')}
                                            </div>
                                        )}
                                    </div>
                                </Tooltip>
                            );
                        };

                        // Build grouped columns from sectionname "Module::Sub" or "Module::__direct__"
                        const moduleGroups = new Map<string, any[]>();
                        assignments.forEach(a => {
                            const parts = (a.sectionname || '').split('::');
                            const modName = parts[0] || '__flat__';
                            if (!moduleGroups.has(modName)) moduleGroups.set(modName, []);
                            moduleGroups.get(modName)!.push(a);
                        });

                        const assignmentColumns: any[] = Array.from(moduleGroups.entries()).map(([modName, assigns]) => {
                            const children = assigns.map(a => ({
                                title: <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: 11 }}>{a.name}</div>
                                    <div style={{ fontSize: 10, color: '#8c8c8c', fontWeight: 400 }}>Due: {a.deadline > 0 ? dayjs.unix(a.deadline).format('DD MMM') : '-'}</div>
                                    <Button
                                        type="link" size="small" icon={<RobotOutlined />}
                                        style={{ fontSize: 10, padding: 0, height: 'auto' }}
                                        onClick={(e) => { e.stopPropagation(); navigate(`/trainer-led-training-details/${trainingId}/bulk-evaluate/${a.id}`); }}
                                    >
                                        Evaluate All
                                    </Button>
                                </div>,
                                key: `assign_${a.id}`,
                                align: 'center' as const,
                                width: 90,
                                render: (_: any, record: any) => renderCell(a, record),
                            }));
                            if (modName === '__flat__') return children;
                            return {
                                title: <span style={{ fontWeight: 600, fontSize: 12 }}>{modName}</span>,
                                key: `mod_${modName}`,
                                children,
                            };
                        }).flat();

                        const columns: any[] = [
                            {
                                title: 'Student',
                                dataIndex: 'fullname',
                                key: 'fullname',
                                fixed: 'left' as const,
                                width: 200,
                                render: (text: string, record: any) => {
                                    const pending = (record.modules || []).filter((m: any) =>
                                        m.workflowenabled && (m.currentstate === 'proposed' || m.currentstate === 'updated')
                                    ).length;
                                    return (
                                        <Space style={{ cursor: 'pointer' }} onClick={() => drillIn(record)}>
                                            <Badge count={pending} size="small" offset={[-2, 2]}>
                                                <Avatar icon={<UserOutlined />} src={`https://ui-avatars.com/api/?name=${encodeURIComponent(formatDisplayName(text, record?.email) || 'U')}&background=random`} />
                                            </Badge>
                                            <div>
                                                <div style={{ fontWeight: 600, fontSize: 13, color: '#1677ff' }}>{formatDisplayName(text, record?.email) || 'Unknown'}</div>
                                                <div style={{ fontSize: 11, color: '#8c8c8c' }}>{record?.email || ''}</div>
                                            </div>
                                        </Space>
                                    );
                                }
                            },
                            ...assignmentColumns,
                            {
                                title: 'Status',
                                key: 'status',
                                fixed: 'right' as const,
                                align: 'center' as const,
                                width: 110,
                                render: (_: any, record: any) => {
                                    const mods: any[] = record.modules || [];
                                    const total = assignments.length;
                                    const submitted = mods.filter((m: any) => m.grade >= 0 || m.timesubmitted > 0).length;
                                    const deadlinePassed = training?.endDate
                                        ? dayjs(training.endDate.toString().split('T')[0]).isBefore(dayjs(), 'day')
                                        : false;
                                    if (total === 0) return <Text type="secondary" style={{ fontSize: 12 }}>—</Text>;
                                    if (submitted === 0) return <Tag color="default" style={{ margin: 0 }}>Not Started</Tag>;
                                    if (!deadlinePassed && training?.active !== false) return <Tag color="processing" style={{ margin: 0 }}>In Progress</Tag>;
                                    if (submitted >= total) return <Tag color="success" style={{ margin: 0 }}>Completed</Tag>;
                                    return <Tag color="warning" style={{ margin: 0 }}>Incomplete</Tag>;
                                }
                            },
                            {
                                title: 'Credits',
                                key: 'credits',
                                fixed: 'right' as const,
                                align: 'center' as const,
                                width: 100,
                                render: (_: any, record: any) => {
                                    const mods: any[] = record.modules || [];
                                    const totalMax = mods.reduce((s: number, m: any) => s + (m.maxcredits || 0), 0);
                                    const totalAchieved = mods.reduce((s: number, m: any) => s + (m.grade >= 0 ? m.grade : 0), 0);
                                    const anyGraded = mods.some((m: any) => m.grade >= 0);
                                    if (!anyGraded || totalMax === 0) return <Text type="secondary" style={{ fontSize: 12 }}>—</Text>;
                                    const pct = Math.round((totalAchieved / totalMax) * 100);
                                    const color = pct >= 70 ? '#52c41a' : pct >= 40 ? '#fa8c16' : '#ff4d4f';
                                    return (
                                        <div>
                                            <div style={{ fontWeight: 600, color, fontSize: 13 }}>
                                                {totalAchieved}<Text type="secondary" style={{ fontSize: 10 }}>/{totalMax}</Text>
                                            </div>
                                            <Progress percent={pct} size="small" showInfo={false} strokeColor={color} style={{ marginBottom: 0 }} />
                                        </div>
                                    );
                                }
                            },
                        ];

                        return (
                            <Table
                                dataSource={courseProgress?.participants || []}
                                rowKey="userid"
                                pagination={false}
                                columns={columns}
                                scroll={{ x: 'max-content', y: 'calc(100vh - 320px)' }}
                                sticky
                                bordered
                                size="small"
                                rowClassName={(record: any) => {
                                    const hasUrgent = (record.modules || []).some((m: any) =>
                                        m.workflowenabled && (m.currentstate === 'proposed' || m.currentstate === 'updated')
                                    );
                                    return hasUrgent ? 'row-needs-action' : '';
                                }}
                            />
                        );
                    })()}
                </Card>
                                ) : (
                                    <>
                                        {modules.length > 0 && (() => {
                                            const done = modules.filter(m => {
                                                const s = (m.status as string) || '';
                                                const st = (m.currentState as string || '').toLowerCase();
                                                return s === 'Submitted' || s === 'Passed' || st === 'approved' || st === 'completed';
                                            }).length;
                                            const pct = Math.round((done / modules.length) * 100);
                                            return (
                                                <div style={{ margin: '0 0 8px', background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 8, overflow: 'hidden' }}>
                                                    <div style={{ display: 'flex', alignItems: 'stretch', overflowX: 'auto' }}>
                                                        <div style={{ flexShrink: 0, padding: '6px 12px', borderRight: '1px solid #f0f0f0', display: 'flex', flexDirection: 'column', justifyContent: 'center', background: '#fff' }}>
                                                            <div style={{ fontSize: 16, fontWeight: 700, color: pct >= 70 ? '#389e0d' : pct >= 40 ? '#d46b08' : '#8c8c8c', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{pct}%</div>
                                                            <div style={{ fontSize: 10, color: '#8c8c8c', marginTop: 2, whiteSpace: 'nowrap' }}>{done}/{modules.length} done</div>
                                                        </div>
                                                        {modules.map((m, i) => {
                                                            const s = (m.status as string) || '';
                                                            const st = (m.currentState as string || '').toLowerCase();
                                                            const isApproved = s === 'Passed' || st === 'approved' || st === 'completed';
                                                            const isSubmitted = s === 'Submitted';
                                                            const isInProgress = s === 'In Progress';
                                                            const hasGrade = m.achievedCredits != null && (m.maxCredits ?? 0) > 0;
                                                            return (
                                                                <Tooltip key={i} title={
                                                                    <span style={{ fontSize: 11 }}>
                                                                        {m.name}<br />
                                                                        {isApproved ? 'Approved' : isSubmitted ? 'Submitted' : isInProgress ? 'In Progress' : 'Not Started'}
                                                                        {m.timeSubmitted && m.timeSubmitted > 0 ? ` · ${dayjs.unix(m.timeSubmitted).format('DD MMM')}` : ''}
                                                                        {hasGrade ? ` · ${m.achievedCredits}/${m.maxCredits} pts` : ''}
                                                                    </span>
                                                                }>
                                                                    <div style={{
                                                                        flexShrink: 0, minWidth: 68, padding: '5px 6px', textAlign: 'center',
                                                                        borderRight: i < modules.length - 1 ? '1px solid #f0f0f0' : 'none',
                                                                        background: isApproved ? '#e6f4ff' : isSubmitted ? '#f6ffed' : isInProgress ? '#fff7e6' : 'transparent',
                                                                        cursor: 'default',
                                                                    }}>
                                                                        <div style={{ fontSize: 9, color: '#8c8c8c', marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 64 }}>{m.name}</div>
                                                                        {isApproved || isSubmitted
                                                                            ? <CheckCircleFilled style={{ color: isApproved ? '#1890ff' : '#52c41a', fontSize: 15 }} />
                                                                            : isInProgress
                                                                                ? <ExclamationCircleFilled style={{ color: '#faad14', fontSize: 15 }} />
                                                                                : <ClockCircleFilled style={{ color: '#d9d9d9', fontSize: 15 }} />
                                                                        }
                                                                        {hasGrade && <div style={{ fontSize: 9, color: '#52c41a', marginTop: 1, fontVariantNumeric: 'tabular-nums' }}>{m.achievedCredits}/{m.maxCredits}</div>}
                                                                    </div>
                                                                </Tooltip>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                        <TrainerLedView
                                            training={training}
                                            modules={modules}
                                            nestedModules={nestedModules}
                                            onRefresh={() => loadDetails(true)}
                                            isAdmin={isAdmin}
                                            selectedUser={selectedUser}
                                            initialModuleId={targetModuleId || undefined}
                                            onModuleChange={setSelectedModuleId}
                                        />
                                    </>
                                )}
                            </>
                        )
                    },
                    ...(modules.reduce((s, m) => s + (m.maxCredits ?? 0), 0) > 0 ? [{
                        key: 'leaderboard',
                        label: <Space><TrophyOutlined />Leaderboard</Space>,
                        children: (
                            <div style={{ paddingTop: 8 }}>
                                {leaderboard.length === 0 && (courseProgress?.participants || []).length > 0 ? (
                                    <ParticipantList
                                        participants={deriveUngradedParticipants(courseProgress)}
                                        emptyDescription="No participants enrolled yet."
                                    />
                                ) : (
                                    <Leaderboard
                                        entries={leaderboard}
                                        variant="trainerLed"
                                        emptyDescription="No graded assignments yet — scores will appear here once assignments are graded."
                                    />
                                )}
                            </div>
                        )
                    }] : [])
                ]}
            />

            <style>{`.tl-rpt-tbl th{border:1px solid #0050a0!important}.tl-rpt-tbl td{border:1px solid #e0e0e0!important}`}</style>
            <Modal
                title="Share Progress Report"
                open={shareModalOpen}
                onCancel={() => { setShareModalOpen(false); setStakeholderEmails([]); setTrainerComment(''); }}
                onOk={handleShareReport}
                okText="Send Report"
                confirmLoading={sharing}
                okButtonProps={{ disabled: stakeholderEmails.length === 0 }}
                width="90vw"
                style={{ maxWidth: 1100 }}
            >
                <Space direction="vertical" style={{ width: '100%' }} size={16}>
                    <div>
                        <Typography.Text strong style={{ display: 'block', marginBottom: 6 }}>Send Report To</Typography.Text>
                        <Select
                            mode="tags"
                            style={{ width: '100%' }}
                            placeholder="Type email and press Enter or comma to add"
                            value={stakeholderEmails}
                            onChange={setStakeholderEmails}
                            tokenSeparators={[',', ';']}
                            open={false}
                            suffixIcon={null}
                        />
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>Each recipient gets this report as an Excel attachment.</Typography.Text>
                    </div>
                    <div>
                        <Typography.Text strong style={{ display: 'block', marginBottom: 6 }}>Comments <Typography.Text type="secondary" style={{ fontWeight: 400, fontSize: 12 }}>(optional)</Typography.Text></Typography.Text>
                        <div style={{ border: '1px solid #d9d9d9', borderRadius: 6, overflow: 'hidden' }}>
                            <div style={{ display: 'flex', gap: 2, padding: '4px 6px', background: '#fafafa', borderBottom: '1px solid #d9d9d9' }}>
                                <Button size="small" icon={<BoldOutlined />} onClick={() => applyFormat('**')} title="Bold" />
                                <Button size="small" icon={<ItalicOutlined />} onClick={() => applyFormat('*')} title="Italic" />
                                <Button size="small" icon={<UnorderedListOutlined />} onClick={applyBullet} title="Bullet" />
                            </div>
                            <Input.TextArea
                                ref={commentRef}
                                rows={4}
                                placeholder="Add any comments or context for the recipients..."
                                value={trainerComment}
                                onChange={e => setTrainerComment(e.target.value)}
                                maxLength={1000}
                                showCount
                                style={{ border: 'none', borderRadius: 0, resize: 'vertical' }}
                            />
                        </div>
                    </div>

                    {(() => {
                        const assignments: any[] = courseProgress?.assignments || [];
                        const participants: any[] = courseProgress?.participants || [];

                        // Build module groups (same as consolidated view)
                        const moduleGroups = new Map<string, any[]>();
                        assignments.forEach((a: any) => {
                            const mod = (a.sectionname || '').split('::')[0] || '__flat__';
                            if (!moduleGroups.has(mod)) moduleGroups.set(mod, []);
                            moduleGroups.get(mod)!.push(a);
                        });
                        // Sorted in grouped order so assignment name row and data cells align with group headers
                        const sortedAssignments: any[] = [];
                        Array.from(moduleGroups.values()).forEach(grp => sortedAssignments.push(...grp));


                        return (
                            <div>
                                <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
                                    Report Preview &nbsp;
                                    <Typography.Text type="secondary" style={{ fontWeight: 400, fontSize: 12 }}>
                                        ({participants.length} participants · this is what will be emailed as Excel)
                                    </Typography.Text>
                                </Typography.Text>
                                <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 420, border: '1px solid #e8e8e8', borderRadius: 6 }}>
                                    <table className="tl-rpt-tbl" style={{ borderCollapse: 'collapse', fontSize: 12, minWidth: '100%' }}>
                                        <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                                            {/* Module group row */}
                                            <tr>
                                                <th rowSpan={2} style={{ padding: '8px 12px', background: '#001529', color: '#fff', textAlign: 'left', whiteSpace: 'nowrap', minWidth: 160, borderRight: '1px solid #0050a0' }}>Name</th>
                                                <th rowSpan={2} style={{ padding: '8px 12px', background: '#001529', color: '#fff', textAlign: 'left', whiteSpace: 'nowrap', minWidth: 180, borderRight: '1px solid #0050a0' }}>Email</th>
                                                {Array.from(moduleGroups.entries()).map(([modName, assigns]) => (
                                                    <th key={modName} colSpan={assigns.length} style={{ padding: '6px 8px', background: '#002140', color: '#91caff', textAlign: 'center', borderBottom: '1px solid #0050a0', borderRight: '1px solid #0050a0', whiteSpace: 'nowrap' }}>
                                                        {modName === '__flat__' ? '' : modName}
                                                    </th>
                                                ))}
                                                <th rowSpan={2} style={{ padding: '8px 10px', background: '#001529', color: '#fff', textAlign: 'center', whiteSpace: 'nowrap', width: 90, borderLeft: '1px solid #0050a0' }}>Credits</th>
                                                <th rowSpan={2} style={{ padding: '8px 10px', background: '#001529', color: '#fff', textAlign: 'center', whiteSpace: 'nowrap', width: 80, borderLeft: '1px solid #0050a0' }}>Max</th>
                                                <th rowSpan={2} style={{ padding: '8px 10px', background: '#001529', color: '#fff', textAlign: 'center', whiteSpace: 'nowrap', width: 80, borderLeft: '1px solid #0050a0' }}>%</th>
                                            </tr>
                                            {/* Assignment name row */}
                                            <tr>
                                                {sortedAssignments.map((a: any) => (
                                                    <th key={a.id} style={{ padding: '6px 8px', background: '#003a6e', color: '#e6f4ff', textAlign: 'center', borderTop: '1px solid #0050a0', borderRight: '1px solid #0050a0', maxWidth: 140, whiteSpace: 'normal', lineHeight: 1.3 }}>
                                                        <div>{a.name}</div>
                                                        <div style={{ fontSize: 10, color: '#91caff', fontWeight: 400, marginTop: 2 }}>Due: {a.deadline > 0 ? dayjs.unix(a.deadline).format('DD MMM') : '-'}</div>
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {participants.map((p: any, pi: number) => {
                                                const modById: Record<number, any> = {};
                                                (p.modules || []).forEach((m: any) => { modById[m.assignmentid] = m; });
                                                let totalCred = 0, maxCred = 0;
                                                return (
                                                    <tr key={p.userid} style={{ background: pi % 2 === 0 ? '#fff' : '#fafafa' }}>
                                                        <td style={{ padding: '8px 12px', borderBottom: '1px solid #f0f0f0', borderRight: '1px solid #f0f0f0', whiteSpace: 'nowrap' }}>
                                                            <div style={{ fontWeight: 500 }}>{formatDisplayName(p.fullname, p.email)}</div>
                                                        </td>
                                                        <td style={{ padding: '8px 12px', borderBottom: '1px solid #f0f0f0', borderRight: '1px solid #f0f0f0', color: '#595959', whiteSpace: 'nowrap' }}>{p.email}</td>
                                                        {sortedAssignments.map((a: any) => {
                                                            const mod = modById[a.id];
                                                            if (!mod) return (
                                                                <td key={a.id} style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid #f0f0f0', borderRight: '1px solid #f0f0f0', color: '#d9d9d9' }}>—</td>
                                                            );
                                                            const grade = mod.grade >= 0 ? mod.grade : -1;
                                                            totalCred += grade >= 0 ? grade : 0;
                                                            maxCred += mod.maxcredits || 0;
                                                            const dateStr = mod.timesubmitted > 0 ? dayjs.unix(mod.timesubmitted).format('DD MMM') : '';
                                                            const gradeStr = grade >= 0 && mod.maxcredits > 0 ? `${grade}/${mod.maxcredits}` : '';
                                                            // Same label and colours as the Excel export (incl. "Under review")
                                                            const { label, bgHex: bg, fgHex: fg, underReview: underReviewCell } = progressCell(mod);
                                                            return (
                                                                <td key={a.id} style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #f0f0f0', borderRight: '1px solid #f0f0f0', background: bg }}>
                                                                    <div style={{ color: fg, fontWeight: 600, fontSize: 11 }}>{label}</div>
                                                                    {dateStr && !underReviewCell && <div style={{ color: HEX.secondaryText, fontSize: 10 }}>{dateStr}</div>}
                                                                    {gradeStr && <div style={{ color: fg, fontSize: 10 }}>{gradeStr} pts</div>}
                                                                </td>
                                                            );
                                                        })}
                                                        {(() => {
                                                            const pct = maxCred > 0 ? Math.round((totalCred / maxCred) * 100) : 0;
                                                            const c = pct >= 70 ? HEX.passedText : pct >= 40 ? HEX.warnText : pct > 0 ? HEX.failedText : HEX.notStartedText;
                                                            return (
                                                                <>
                                                                    <td style={{ padding: '8px 10px', textAlign: 'center', borderBottom: '1px solid #f0f0f0', borderLeft: '1px solid #f0f0f0', fontWeight: 700, color: c }}>{totalCred}</td>
                                                                    <td style={{ padding: '8px 10px', textAlign: 'center', borderBottom: '1px solid #f0f0f0', color: HEX.emailText }}>{maxCred}</td>
                                                                    <td style={{ padding: '8px 10px', textAlign: 'center', borderBottom: '1px solid #f0f0f0', fontWeight: 700, color: c }}>{pct > 0 ? `${pct}%` : '—'}</td>
                                                                </>
                                                            );
                                                        })()}
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        );
                    })()}
                </Space>
            </Modal>
        </div>
    );
};

export default TrainerLedTrainingDetails;
